import React, { useContext, FC, ReactNode } from 'react';

import styled from '@emotion/styled';
import {
  BeaconEventMarketingPageElementLocation,
  BeaconEventMarketingPageElementType
} from '@lucid/utils';
import { variant, ComponentsType } from '@lucid/theme';

import LayoutContext from '../context/layout-context';
import Image from '../image/image';
import Link from '../link/link';
import { ComponentImage } from '../../types/generics';

export interface NavLogoWithoutChildrenOptions {
  /** The Logo object containing alt and src keys. */
  logo: ComponentImage;
  /** The children of the NavLogo is not used if passed a logo object. */
  children?: never;
}

export interface NavLogoWithChildrenOptions {
  /** The Logo object is not used if using chilren. */
  logo?: never;
  /** The children of the NavLogo component. */
  children: ReactNode;
}

export interface NavLogoOptions {
  /** Checks if the navbar is on the top most nav. */
  isTopNav?: boolean;
  /** The link that wraps the Logo. */
  logoLink?: string;
}

export interface StyledNavLogoProps {
  /** The available variants for the NavLogo component. */
  variant?: ComponentsType['Nav']['Logo'];
}

export interface NavLogoBaseProps extends NavLogoOptions, StyledNavLogoProps {
  /** Allows the component to get additional styles. */
  className?: string;
}

export type NavLogoProps = (NavLogoWithoutChildrenOptions | NavLogoWithChildrenOptions) & NavLogoBaseProps;

const StyledNavLogo = styled.div<StyledNavLogoProps>(
  variant({
    scale: 'nav.logo',
    variants: {
      defaultVariants: {}
    }
  }),
  {
    label: 'NavLogo',
    display: 'flex',
    alignItems: 'center'
  }
);

const NavLogo: FC<NavLogoProps> = ({
  children,
  className,
  isTopNav = false,
  logo,
  logoLink,
  variant = 'default',
  ...rest
}) => {
  const { language } = useContext(LayoutContext);
  const { shortLangPrefix } = language;
  const navLogoLink = logoLink
    ? isTopNav
      ? `${logoLink}/${shortLangPrefix === 'en' ? '' : shortLangPrefix}`
      : logoLink
    : `/${shortLangPrefix === 'en' ? '' : shortLangPrefix}`;

  return (
    <StyledNavLogo
      {...rest}
      className={className}
      variant={variant}
    >
      <Link
        beaconEvent={{
          name: 'clickedMarketingPageElement',
          elementLocation: BeaconEventMarketingPageElementLocation.nav,
          elementDetail: 'logo',
          elementType: BeaconEventMarketingPageElementType.image
        }}
        to={navLogoLink}
      >
        {logo && logo.imageSrc ? (
          <Image
            dataElementPageLocation={isTopNav ? 'topNav' : 'siteNav'}
            imageAlt={logo.imageAlt || ''}
            imageSrc={logo.imageSrc}
          />
        ) : children}
      </Link>
    </StyledNavLogo>
  );
};

export { NavLogo };
export default NavLogo;
